/** Local answers for Mira, the in-app guide. */

export const MASCOT_NAME = 'Mira';

export const SUGGESTIONS = [
  'Upload a fabric',
  'Generate a concept',
  'Credits and plans',
  'Edit in Studio',
];

const TOPICS = [
  {
    id: 'hello',
    keywords: ['hi', 'hey', 'hello', 'hiya', 'good morning', 'good evening', 'howdy'],
    text: "Hi, I'm Mira. I can walk you through fabrics, concept generation, Studio, credits, or get a person from the team to call you.",
    actions: [],
  },
  {
    id: 'thanks',
    keywords: ['thanks', 'thank you', 'thx'],
    text: "Happy to help. Ask me anything else whenever you want.",
    actions: [],
  },
  {
    id: 'who',
    keywords: ['who are you', 'what is mira', 'your name', 'what can you do', 'help me', 'what can you help'],
    text: "I'm Mira, Fashdeck's guide. Ask me how to upload a fabric, generate a concept, edit in Studio, share a catalogue, or understand credits and plans. If I can't answer, I'll pass you to the team.",
    actions: [{ label: 'Request a callback', kind: 'support' }],
  },
  {
    id: 'fabrics',
    keywords: [
      'upload', 'uploading', 'add fabric', 'my fabrics', 'swatch', 'fabric photo', 'fabric image',
      'fabric library', 'add a fabric', 'save fabric', 'catalogue', 'catalog',
    ],
    text: "Open My Fabrics and upload a clear photo of the cloth. A flat, well-lit swatch with the pattern fully visible works best. Add tags such as cotton, summer, or formal so you can find it later. You can then use that fabric when you generate a concept.",
    actions: [{ label: 'Open My Fabrics', publicLabel: 'Sign in to upload fabrics', view: 'myproducts' }],
  },
  {
    id: 'generate',
    keywords: [
      'generate', 'generation', 'concept', 'concepts', 'create design', 'new design',
      'garment type', 'silhouette', 'make a design', 'kit',
    ],
    text: "Open Generate Concepts, pick a fabric from your library, choose who it's for and the garment type, then generate. The result is saved in AI Creations. Each generation uses image credits.",
    actions: [{ label: 'Generate a concept', publicLabel: 'Sign in to generate', view: 'generate' }],
  },
  {
    id: 'studio',
    keywords: [
      'studio', 'magic edit', 'variation', 'variations', 'neckline', 'sleeve', 'sleeves',
      'edit design', 'edit the', 'change the design', 'restyle',
    ],
    text: "Open a creation and go to Studio to refine it. You can adjust details such as the neckline, sleeves, and length, describe a change with Magic Edit, or generate variations to explore another direction.",
    actions: [{ label: 'Open AI Creations', publicLabel: 'Sign in to open Studio', view: 'mykits' }],
  },
  {
    id: 'creations',
    keywords: ['ai creations', 'my kits', 'my kit', 'kits', 'saved kit', 'creations', 'download', 'moodboard', 'mood board', 'collection plan'],
    text: "AI Creations is where generated kits live. Open one to view it, download it, edit it in Studio, or switch to Moodboards to build a visual story from your designs.",
    actions: [
      { label: 'Open AI Creations', publicLabel: 'Sign in to view creations', view: 'mykits' },
      { label: 'Open Moodboards', publicLabel: 'Sign in to view moodboards', view: 'moodboard' },
    ],
  },
  {
    id: 'designs',
    keywords: ['garment design', 'garment designs', 'inspiration', 'inspirational', 'body cut', 'silhouette library'],
    text: "Garment Designs is a library of silhouettes and references. Browse them for a direction, then generate a concept with one of your fabrics.",
    actions: [{ label: 'Open Garment Designs', publicLabel: 'Sign in to browse designs', view: 'inspirational' }],
  },
  {
    id: 'credits',
    keywords: ['credit', 'credits', 'quota', 'ran out', 'run out', 'not enough', 'insufficient'],
    text: "Image credits are used when you generate or edit designs. Your remaining balance sits with your plan in the sidebar. Basic includes 100 image credits a month, and Advanced Generation includes 500. If a generation says you don't have enough, open Pricing to add a plan. Prices shown there are before GST, and yearly billing saves 10%.",
    actions: [{ label: 'View pricing', view: 'pricing' }],
  },
  {
    id: 'pricing',
    keywords: ['price', 'pricing', 'plan', 'subscribe', 'subscription', 'payment', 'pay', 'billing', 'upgrade', 'buy', 'cost', 'rupee'],
    text: "Basic is ₹1,999 a month with 100 image credits. Advanced Generation is ₹4,999 a month with 500 image credits. Both include an unlimited fabric catalogue, AI concept generation, and Studio. Yearly billing saves 10%. Checkout adds 18% GST. There's also a Try On Screen package for in-store try-on.",
    actions: [{ label: 'View pricing', view: 'pricing' }],
  },
  {
    id: 'tryon',
    keywords: ['try on', 'try-on', 'tryon', 'virtual try', 'on a model', 'on model'],
    text: "Virtual Tryon previews a garment on a person. Open it from the sidebar under Virtual Tryon. The Try On Screen plan is a separate in-store package on the Pricing page.",
    actions: [
      { label: 'Open Virtual Tryon', publicLabel: 'Sign in for Virtual Tryon', view: 'virtualtry' },
      { label: 'View pricing', view: 'pricing' },
    ],
  },
  {
    id: 'share',
    keywords: ['share', 'public link', 'public catalogue', 'public catalog', 'my website', 'website', 'buyer'],
    text: "You can present work outside your account in two ways. Share a fabric collection so buyers can browse it, or open My Website to show your work on a page of your own.",
    actions: [
      { label: 'Open My Fabrics', publicLabel: 'Sign in to share fabrics', view: 'myproducts' },
      { label: 'Open My Website', publicLabel: 'Sign in to open My Website', view: 'mywebsite' },
    ],
  },
  {
    id: 'brand',
    keywords: ['brand', 'logo', 'brand profile', 'brand name'],
    text: "A brand profile stores the name, logo, and colours that can go on your kits. Create or edit one from Brand Profile, then choose it when you save a kit.",
    actions: [{ label: 'Open Brand Profile', publicLabel: 'Sign in to edit your brand', view: 'brandlibrary' }],
  },
  {
    id: 'account',
    keywords: ['account', 'profile', 'password', 'log in', 'login', 'sign in', 'otp', 'whatsapp', 'lite mode'],
    text: "Sign-in uses a one-time code sent on WhatsApp. After you're in, My Account is where you update your profile and settings, including Lite mode if you want a simpler generate flow.",
    actions: [{ label: 'Open My Account', publicLabel: 'Sign in', view: 'settings' }],
  },
  {
    id: 'support',
    keywords: ['support', 'callback', 'call me', 'human', 'person', 'agent', 'talk to', 'contact', 'help desk', 'not working', 'bug', 'error', 'failed', 'payment failed'],
    text: "I can pass this to the team. Leave your number and a short note, and someone will call you back. Mention what you were trying to do, for example a failed generation or a payment.",
    actions: [{ label: 'Request a callback', kind: 'support' }],
  },
  {
    id: 'about',
    keywords: ['what is fashdeck', 'about fashdeck', 'what do you do', 'what is this'],
    text: "Fashdeck is an AI studio for fashion design and sourcing. Fabric sellers build digital catalogues, and designers turn those fabrics into garment concepts, moodboards, and shareable work without cutting a sample first.",
    actions: [{ label: 'View pricing', view: 'pricing' }],
  },
];

const PAGE_GUIDES = {
  generate: {
    label: 'Generate',
    aliases: ['generate concepts', 'generate page', 'concept generator'],
    blurb: 'Pick a fabric and a garment type, then generate a concept. It uses image credits and lands in AI Creations.',
    text: "Generate is where a fabric becomes a garment concept. Choose a fabric from My Fabrics, pick who it's for and the garment type, then generate. The result is saved in AI Creations. Each generation uses image credits — 2 if 4K is on in My Account.",
    suggestions: ['How this page works', 'What uses credits', 'Where do results go'],
    actions: [{ label: 'Open AI Creations', publicLabel: 'Sign in to view creations', view: 'mykits' }],
    details: [
      { keywords: ['credit', 'cost', '4k', 'what uses credits', 'uses credits'], text: "Generating a concept spends image credits. Your balance is in the sidebar. Turn on 4K in My Account and each image uses 2 credits instead of 1. If you run out, open Pricing." },
      { keywords: ['result', 'where', 'saved', 'go', 'kit', 'where do results go'], text: "Finished concepts are saved in AI Creations. Open one there to download it or edit it in Studio." },
      { keywords: ['fabric', 'pick', 'choose', 'garment', 'silhouette'], text: "Start with a fabric already in My Fabrics. Then choose the audience and garment type so the concept uses that cloth on that silhouette." },
    ],
  },
  'generate-lite': {
    label: 'Generate',
    aliases: ['generate lite', 'lite generate'],
    blurb: 'This is the shorter Generate flow. Pick a fabric, choose a garment, and generate. Results go to AI Creations.',
    text: "You're in Lite mode, so Generate is the shorter version of concept generation. Pick a fabric, choose the garment, and generate. The result is saved in AI Creations and uses image credits. Turn Lite mode off in My Account for the full flow.",
    suggestions: ['How this page works', 'What is Lite mode', 'Where do results go'],
    actions: [
      { label: 'Open My Account', publicLabel: 'Sign in', view: 'settings' },
      { label: 'Open AI Creations', publicLabel: 'Sign in to view creations', view: 'mykits' },
    ],
    details: [
      { keywords: ['lite', 'lite mode', 'what is lite mode'], text: "Lite mode hides the longer generate steps. Switch it off in My Account to get the full Generate Concepts and My Fabrics pages back." },
      { keywords: ['result', 'where', 'saved', 'kit'], text: "Finished concepts are saved in AI Creations." },
    ],
  },
  myproducts: {
    label: 'My Fabrics',
    aliases: ['my fabrics', 'fabric library'],
    blurb: 'This is your fabric library. Upload a clear swatch, tag it, and use it when you generate.',
    text: "My Fabrics is your own cloth library. Upload a flat, well-lit photo with the pattern fully visible, then add tags such as cotton, summer, or formal so you can find it. Group fabrics into collections, and pick one here when you generate a concept.",
    suggestions: ['How this page works', 'How do I upload', 'How do tags work'],
    actions: [{ label: 'Generate a concept', publicLabel: 'Sign in to generate', view: 'generate' }],
    details: [
      { keywords: ['upload', 'photo', 'add', 'swatch'], text: "Use the upload control and choose a clear fabric photo. Avoid heavy wrinkles and crop in the full pattern. You can add several images." },
      { keywords: ['tag', 'search', 'organise', 'organize'], text: "Tags are words you add to a fabric, like cotton or formal. They are what search uses inside My Fabrics." },
      { keywords: ['collection', 'share', 'public'], text: "Collections group fabrics so you can open them together and share a public link from the collection." },
    ],
  },
  'myproducts-lite': {
    label: 'My Fabrics',
    aliases: ['my fabrics lite', 'lite fabrics'],
    blurb: 'This is the simpler fabric library. Upload a swatch, then use it in Generate.',
    text: "You're in Lite mode, so My Fabrics is the simpler library. Upload a clear fabric photo, then use it in Generate. Turn Lite mode off in My Account for collections, tags, and the full library.",
    suggestions: ['How this page works', 'How do I upload', 'What is Lite mode'],
    actions: [{ label: 'Open My Account', publicLabel: 'Sign in', view: 'settings' }],
    details: [
      { keywords: ['lite'], text: "Lite mode keeps My Fabrics and Generate short. Switch it off in My Account when you want the full library." },
      { keywords: ['upload', 'photo', 'add'], text: "Upload a flat, well-lit photo of the cloth with the pattern fully visible." },
    ],
  },
  inspirational: {
    label: 'Garment designs',
    aliases: ['garment design', 'garment designs', 'design library'],
    blurb: 'These are garment references. Add a design, run analysis, and use it as a silhouette.',
    text: "Garment designs is your library of garment references. Add a design, turn Make it public on if buyers should see it, and Run analysis so Fashdeck can read the category and type. Collections then groups them, for example Women · Dress. Use a design as the silhouette when you generate a concept.",
    suggestions: ['How this page works', 'Run analysis', 'Make a design public'],
    actions: [{ label: 'Generate a concept', publicLabel: 'Sign in to generate', view: 'generate' }],
    details: [
      { keywords: ['analysis', 'analyse', 'analyze', 'run analysis'], text: "Run analysis on a design so Fashdeck detects the product category and type. Collections are built from that, such as Women · Dress or Men · Blazer. Run it again if you replace the image." },
      { keywords: ['public', 'share', 'visible', 'make it public', 'make a design public'], text: "Make it public puts that design on your public link. Leave it off and only your account can see it." },
      { keywords: ['add a design', 'add designs'], text: "Add designs uploads a garment photo into this library. After it lands, run analysis if you want it filed into a collection." },
      { keywords: ['collection'], text: "The Collections tab filters designs by the category and type from garment analysis. A design shows up there after analysis has run." },
    ],
  },
  mykits: {
    label: 'AI Creations',
    aliases: ['ai creations', 'my kits', 'creations'],
    blurb: 'Generated kits live here. Open one to download it or edit it in Studio.',
    text: "AI Creations stores every kit you generate. Open one to view the images, download them, or send it to Studio for edits. Switch to Moodboards to arrange designs into a visual story.",
    suggestions: ['How this page works', 'Edit in Studio', 'What is a moodboard'],
    actions: [
      { label: 'Open Moodboards', publicLabel: 'Sign in to view moodboards', view: 'moodboard' },
    ],
    details: [
      { keywords: ['studio', 'edit'], text: "Open a kit and choose Studio. From there you can change the neckline, sleeves, or length, or describe a change with Magic Edit." },
      { keywords: ['moodboard', 'mood board'], text: "Moodboards sit next to your kits. Use them to lay several designs out as one collection story." },
      { keywords: ['download', 'share'], text: "Open a kit to download the images or share them. Brand logo and colours come from the brand profile you attached when you saved it." },
    ],
  },
  moodboard: {
    label: 'Moodboards',
    aliases: ['moodboard', 'mood board', 'moodboards'],
    blurb: 'Arrange generated designs into a visual story for a collection.',
    text: "Moodboards are boards of your AI creations. Pull designs onto a board to plan a collection, then jump back to AI Creations to open or edit a single kit.",
    suggestions: ['How this page works', 'Where are my kits'],
    actions: [{ label: 'Open AI Creations', publicLabel: 'Sign in to view creations', view: 'mykits' }],
    details: [
      { keywords: ['kit', 'creation'], text: "Individual generated kits live under AI Creations. Moodboards are for grouping those designs into one story." },
    ],
  },
  kitdetails: {
    label: 'Kit Details',
    aliases: ['kit details', 'this kit'],
    blurb: 'This is one generated kit. Download it, share it, or open Studio to change it.',
    text: "Kit Details is a single generated design. Review the images, download them, share the kit, or open Studio to change the silhouette, fabric placement, or other details. Edits in Studio use image credits.",
    suggestions: ['How this page works', 'Edit in Studio'],
    actions: [{ label: 'Open AI Creations', publicLabel: 'Sign in to view creations', view: 'mykits' }],
    details: [
      { keywords: ['studio', 'edit'], text: "Open Studio from this kit to adjust the design or describe a change with Magic Edit. That spends image credits." },
    ],
  },
  aistudio: {
    label: 'Studio',
    aliases: ['studio', 'ai studio', 'magic edit'],
    blurb: 'Edit this design: change the cut, or describe a change with Magic Edit.',
    text: "Studio edits the design you opened. Adjust details such as neckline, sleeves, and length, describe a change in Magic Edit, or generate variations. Each edit uses image credits, and 4K uses 2 credits.",
    suggestions: ['How this page works', 'What is Magic Edit', 'What uses credits'],
    actions: [{ label: 'View pricing', view: 'pricing' }],
    details: [
      { keywords: ['magic'], text: "Magic Edit takes a written change, such as a new sleeve or a longer hem, and applies it to this design." },
      { keywords: ['variation'], text: "Variations make another pass of the same design so you can compare directions without starting over." },
      { keywords: ['credit', '4k'], text: "Studio edits spend image credits. With 4K on in My Account, each image costs 2 credits." },
    ],
  },
  fabricmarketplace: {
    label: 'Fabric Marketplace',
    aliases: ['fabric marketplace', 'marketplace', 'browse collections'],
    blurb: 'Browse public fabric collections from vendors. Search by words, or upload a photo to find similar cloth.',
    text: "Fabric Marketplace lists fabric collections vendors have made public. Search by collection name, tags, or properties, or upload a photo to find visually similar cloth. Open a collection to see its fabrics, then add one to Favourites to keep it.",
    suggestions: ['How this page works', 'Search collections', 'Search with a photo'],
    actions: [{ label: 'Open Subscriptions', publicLabel: 'Sign in to subscribe', view: 'mysubscriptions' }],
    details: [
      { keywords: ['search', 'filter', 'tag', 'find collection'], text: "The search box matches collection name, tags, and properties. Inside a collection it searches that collection's fabrics. Clear the box to see everything again." },
      { keywords: ['photo', 'camera', 'image', 'visual', 'similar', 'picture', 'search with a photo'], text: "Use the camera or upload a fabric photo. Marketplace ranks cloth by visual similarity and shows a match percentage. Clear the photo to go back to text search." },
      { keywords: ['favourite', 'favorite', 'heart', 'save'], text: "On a fabric, Add to Favourites keeps it. Open it again from Favourites instead of searching the whole marketplace." },
      { keywords: ['collection', 'vendor', 'open'], text: "Each card is a vendor collection. Open it to see the fabrics inside, then use the back arrow to return to all collections." },
      { keywords: ['subscribe', 'follow'], text: "To follow a vendor's new collections, open Subscriptions in the Marketplace section of the sidebar." },
    ],
  },
  'fabric-collection-detail': {
    label: 'Fabric collection',
    aliases: ['this collection', 'fabric collection'],
    blurb: 'This is one fabric collection. Open a fabric to use it, or share the collection.',
    text: "This page is a single fabric collection. Open a fabric to inspect it or generate from it. If the collection is yours, you can edit it and share a public link so buyers can browse it in the marketplace.",
    suggestions: ['How this page works', 'Share this collection'],
    actions: [{ label: 'Open My Fabrics', publicLabel: 'Sign in', view: 'myproducts' }],
    details: [
      { keywords: ['share', 'public', 'link'], text: "A public collection gets a link buyers can open without an account. They'll see it in Fabric Marketplace." },
    ],
  },
  virtualtry: {
    label: 'Virtual Tryon',
    aliases: ['virtual tryon', 'virtual try-on', 'try on', 'tryon'],
    blurb: 'Preview a garment on a person. Pick a product, then start the try-on.',
    text: "Virtual Tryon shows a product on a person. Choose the product and the category, then start the try-on. It uses image credits. If the page says try-on is unavailable, it isn't enabled on this account — request a callback and the team can turn it on.",
    suggestions: ['How this page works', 'Why is try-on unavailable'],
    actions: [{ label: 'Request a callback', kind: 'support' }],
    details: [
      { keywords: ['unavailable', 'disabled', 'locked', 'enable'], text: "Virtual try-on can be turned off per account. If you see the unavailable message, request a callback and the team can enable it." },
      { keywords: ['credit'], text: "A try-on spends image credits, the same balance shown in the sidebar." },
    ],
  },
  promoters: {
    label: 'Promoters',
    aliases: ['promoter', 'promoters'],
    blurb: 'Ask vendors to promote your fabrics or garment designs, and answer requests sent to you.',
    text: "Promoters is for promotion requests. Ask a vendor to promote your fabrics or garment designs, respond to requests others sent you, and see who you've already approved.",
    suggestions: ['How this page works', 'Send a request'],
    actions: [],
    details: [
      { keywords: ['request', 'ask', 'send', 'approve'], text: "Outgoing requests are the ones you sent. Incoming requests are waiting for your answer. Approved shows vendors you already accepted." },
    ],
  },
  mysubscriptions: {
    label: 'Subscriptions',
    aliases: ['subscription', 'subscriptions', 'following'],
    blurb: 'Vendors you follow show up here, including new collections they publish.',
    text: "Subscriptions is the list of vendors and collections you follow from the marketplace. Open someone to see their collections, and unsubscribe if you don't want their updates.",
    suggestions: ['How this page works', 'How do I unfollow'],
    actions: [{ label: 'Open Fabric Marketplace', publicLabel: 'Sign in to browse', view: 'fabricmarketplace' }],
    details: [
      { keywords: ['unfollow', 'unsubscribe', 'remove'], text: "Open the vendor in Subscriptions and unsubscribe. They drop off this list and you stop seeing their new collections here." },
    ],
  },
  mywebsite: {
    label: 'My Website',
    aliases: ['my website', 'vendor page', 'public page'],
    blurb: 'This is the public page buyers open. Set your link, theme, and brand details.',
    text: "My Website is your public vendor page. Set a username for the public link, pick a colour theme, and fill in the brand name, photos, and contact details buyers see. You need a phone number on the account before the page can be published.",
    suggestions: ['How this page works', 'Share my link'],
    actions: [],
    details: [
      { keywords: ['link', 'username', 'url', 'share'], text: "The username becomes your public URL. Save it, then share that link. Buyers see the theme and profile you set on this page." },
      { keywords: ['theme', 'colour', 'color', 'photo'], text: "The theme changes the colour of the public page. Avatar, cover, and business photos are the images buyers see at the top of that page." },
    ],
  },
  settings: {
    label: 'My Account',
    aliases: ['my account', 'account settings', 'settings'],
    blurb: 'See your plan and credits, and switch Lite mode or 4K.',
    text: "My Account shows your plan, credit balance, and profile. Profile details are shown here and edited from your sign-in profile. Preferences are on this page: Lite mode shortens My Fabrics and Generate, and 4K makes each generated image cost 2 credits. Purchase credits jumps to Pricing.",
    suggestions: ['How this page works', 'What is Lite mode', 'What is 4K'],
    actions: [{ label: 'View pricing', view: 'pricing' }],
    details: [
      { keywords: ['lite', 'lite mode', 'what is lite mode'], text: "Lite mode shows only the shorter My Fabrics and Generate pages. Turn it off here to get the full library and generate flow back." },
      { keywords: ['4k', 'resolution', 'what is 4k'], text: "4K generates a larger image and charges 2 image credits instead of 1. Your remaining credits and plan expiry are at the top of this page." },
      { keywords: ['profile', 'name', 'email', 'phone'], text: "Name, email, phone, and company are shown on My Account. They come from your profile and aren't edited in this form." },
    ],
  },
  pricing: {
    label: 'Pricing',
    aliases: ['pricing page', 'the plans'],
    blurb: 'Compare plans and buy image credits. Prices are before GST.',
    text: "Pricing lists the plans. Basic is ₹1,999 a month with 100 image credits. Advanced Generation is ₹4,999 a month with 500. Both include an unlimited fabric catalogue, concept generation, and Studio. Yearly billing saves 10%. Checkout adds 18% GST. Try On Screen is the in-store package.",
    suggestions: ['How this page works', 'What is Basic', 'What is yearly billing'],
    actions: [],
    details: [
      { keywords: ['basic'], text: "Basic is ₹1,999 a month, 100 image credits, unlimited fabric catalogue, concept generation, and Studio. GST is added at checkout." },
      { keywords: ['advanced', 'generation plan'], text: "Advanced Generation is ₹4,999 a month with 500 image credits, plus the same catalogue, generation, and Studio access." },
      { keywords: ['year', 'yearly', 'annual'], text: "The Yearly tab bills 12 months at 10% off the monthly price. Credits are still granted per month." },
      { keywords: ['try on screen', 'in-store', 'instore'], text: "Try On Screen is a one-time in-store package. It includes Advanced Generation free for 3 months and an interactive try-on screen." },
      { keywords: ['gst', 'tax'], text: "Prices on the cards are before tax. Checkout adds 18% GST." },
    ],
  },
  brandlibrary: {
    label: 'Brand Profile',
    aliases: ['brand profile', 'brand library'],
    blurb: 'Save a brand name, logo, and colours, then apply them to a kit.',
    text: "Brand Profile stores the name, logo, colours, and fonts that can be applied to a kit. Create one here, then choose it when you save or share a design so the kit carries that brand.",
    suggestions: ['How this page works'],
    actions: [{ label: 'Open AI Creations', publicLabel: 'Sign in to view creations', view: 'mykits' }],
    details: [],
  },
  'browse-vendors': {
    label: 'Browse Vendors',
    aliases: ['browse vendors'],
    blurb: 'This admin list is every vendor on Fashdeck.',
    text: "Browse Vendors is an admin list of vendors on the platform. Open a vendor to see their account and public fabrics. It isn't available on other roles.",
    suggestions: ['How this page works'],
    actions: [],
    details: [],
  },
  adminpanel: {
    label: 'Admin Panel',
    aliases: ['admin panel', 'admin'],
    blurb: 'Support requests and platform tools for the Fashdeck team.',
    text: "Admin Panel is for the Fashdeck team. Callback requests and help messages from Mira and the support form show up here, along with the other account and platform tools.",
    suggestions: ['How this page works'],
    actions: [],
    details: [
      { keywords: ['support', 'callback', 'request'], text: "Support messages and callback requests submitted in the app are listed in the admin panel so the team can follow up." },
    ],
  },
  mycatalogue: {
    label: 'Reports',
    aliases: ['reports', 'catalogues', 'my catalogues'],
    blurb: 'Shared catalogues are listed here. Admins see every catalogue.',
    text: "This page lists catalogues. Admins see every catalogue on the platform. Everyone else sees their own. Open one to review it, or turn it active or inactive.",
    suggestions: ['How this page works'],
    actions: [],
    details: [],
  },
  'influencer-dashboard': {
    label: 'Influencer Dashboard',
    aliases: ['influencer dashboard', 'influencer'],
    blurb: 'Promoter performance for influencer accounts.',
    text: "Influencer Dashboard is for promoter accounts. It shows the activity tied to your promoter link.",
    suggestions: ['How this page works'],
    actions: [],
    details: [],
  },
  home: {
    label: 'Home',
    aliases: ['home page', 'landing'],
    blurb: 'This is the Fashdeck home page: fabrics, AI concepts, and catalogues.',
    text: "Home introduces Fashdeck. Browse how fabric sourcing, AI design, and shared catalogues fit together, then sign in to upload your own cloth and generate concepts. Pricing is on its own tab.",
    suggestions: ['How this page works', 'View pricing', 'What is Fashdeck'],
    actions: [{ label: 'View pricing', view: 'pricing' }],
    details: [
      { keywords: ['fashdeck', 'what is'], text: "Fashdeck turns a fabric photo into a garment concept, and lets vendors publish a catalogue buyers can open." },
    ],
  },
  dashboard: {
    label: 'Dashboard',
    aliases: ['dashboard'],
    blurb: 'Your home inside the app. Open Generate, fabrics, or creations from the sidebar.',
    text: "Dashboard is the signed-in home. Use the sidebar to generate a concept, open My Fabrics, browse Garment designs, or review AI Creations. Credits and your plan sit at the bottom of the sidebar.",
    suggestions: ['How this page works', 'Where are my credits'],
    actions: [{ label: 'Generate a concept', publicLabel: 'Sign in to generate', view: 'generate' }],
    details: [],
  },
};

const CURRENT_PAGE_CUES = [
  'how this works',
  'how this page works',
  'how does this work',
  'how does this page work',
  'how do i use this',
  'how do i use this page',
  'what is this',
  'what is this page',
  'whats this',
  'explain this',
  'explain this page',
  'what can i do here',
  'what can i do on this page',
  'tell me about this page',
  'what does this page do',
  'walk me through this',
  'walk me through',
  'help with this page',
];

const FALLBACK = {
  id: 'fallback',
  text: "I don't have a sure answer for that. Try asking about uploading a fabric, generating a concept, Studio, credits, or pricing. Or I can have someone from the team call you.",
  actions: [{ label: 'Request a callback', kind: 'support' }],
};

function normalize(raw) {
  return String(raw || '')
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function keywordHits(query, keyword) {
  const needle = keyword.toLowerCase();
  if (needle.includes(' ')) return query.includes(needle);
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Short words must match exactly so "hi" doesn't hit "high". Longer words match plurals.
  const pattern = needle.length <= 3 ? `\\b${escaped}\\b` : `\\b${escaped}`;
  return new RegExp(pattern, 'i').test(query);
}

function scoreKeywords(query, keywords = []) {
  return keywords.reduce((score, keyword) => (
    keywordHits(query, keyword) ? score + (keyword.includes(' ') ? 3 : 1) : score
  ), 0);
}

function asAnswer(page) {
  return { text: page.text, actions: page.actions || [] };
}

function isAboutCurrentPage(query) {
  const words = query.split(' ').length;
  if (words > 8) return false;
  return CURRENT_PAGE_CUES.some((cue) => query === cue || query.includes(cue));
}

export function introForPage(pageId, pageLabel) {
  const page = PAGE_GUIDES[pageId];
  if (page) {
    return `Hi, I'm ${MASCOT_NAME}. You're on ${page.label}. ${page.blurb}`;
  }
  if (!pageLabel) {
    return `Hi, I'm ${MASCOT_NAME}. Ask me how to upload a fabric, generate a concept, use Studio, or sort out credits.`;
  }
  return `Hi, I'm ${MASCOT_NAME}. You're on ${pageLabel}. Ask me how this page works, or anything else in Fashdeck.`;
}

export function suggestionsFor(pageId) {
  const pageSuggestions = PAGE_GUIDES[pageId]?.suggestions || [];
  const seen = new Set(pageSuggestions.map((item) => item.toLowerCase()));
  const basic = SUGGESTIONS.filter((item) => !seen.has(item.toLowerCase()));
  return [...pageSuggestions, ...basic];
}

export function answerQuestion(raw, pageId) {
  const query = normalize(raw);
  if (!query) return null;

  const page = PAGE_GUIDES[pageId] || null;
  const named = Object.values(PAGE_GUIDES)
    .map((guide) => ({ guide, score: scoreKeywords(query, guide.aliases) }))
    .sort((a, b) => b.score - a.score)[0];

  if (named && named.score >= 3 && named.guide !== page) return asAnswer(named.guide);
  if (isAboutCurrentPage(query) && page) return asAnswer(page);

  const detailScore = page
    ? (page.details || []).reduce((best, detail) => {
      const score = scoreKeywords(query, detail.keywords);
      return score > best.score ? { detail, score } : best;
    }, { detail: null, score: 0 })
    : { detail: null, score: 0 };

  const ranked = TOPICS.map((topic) => ({ topic, score: scoreKeywords(query, topic.keywords) }))
    .sort((a, b) => b.score - a.score);
  const best = ranked[0];
  const wordCount = query.split(' ').length;
  const greetingOnly = best.topic.id === 'hello' && wordCount <= 3;
  const thanksOnly = best.topic.id === 'thanks' && wordCount <= 4;
  let topic = best.score > 0 ? best.topic : null;
  if (topic?.id === 'hello' && !greetingOnly && ranked[1]?.score > 0) topic = ranked[1].topic;
  if (topic?.id === 'thanks' && !thanksOnly && ranked[1]?.score > 0) topic = ranked[1].topic;
  if (topic?.id === 'hello' && !greetingOnly) topic = null;
  if (topic?.id === 'thanks' && !thanksOnly) topic = null;

  const topicScore = topic ? scoreKeywords(query, topic.keywords) : 0;
  if (detailScore.score > 0 && detailScore.score >= topicScore) {
    return { text: detailScore.detail.text, actions: page.actions || [] };
  }
  if (topic) return topic;
  if (named?.score > 0) return asAnswer(named.guide);
  if (!page) return FALLBACK;
  return {
    text: `I don't have a sure answer for that. On ${page.label}: ${page.blurb} Ask how this page works for the full walkthrough, or tell me if you need credits, pricing, or a callback.`,
    actions: [{ label: 'Request a callback', kind: 'support' }],
  };
}

export function resolveView(view, liteModeEnabled) {
  if (!liteModeEnabled) return view;
  if (view === 'myproducts') return 'myproducts-lite';
  if (view === 'generate') return 'generate-lite';
  return view;
}
